/// <reference types="vite/client" />
import { z } from "zod";

const booleanEnv = z.enum(["true", "false"]).transform((val) => val === "true");

const envSchema = z.object({
	// deployment
	VITE_WS_URL: z.coerce.string().url().default("ws://localhost:5001"),
	VITE_GRAPHQL_URL: z.coerce
		.string()
		.url()
		.default("http://localhost:5001/graphql"),
	VITE_API_URL: z.coerce.string().url().default("http://localhost:5001/api/v1"),
	VITE_LLM_BASE_URL: z.coerce
		.string()
		.url()
		.default("https://backend-proto.illumi.systems/llm"),
	VITE_MCP_HTTP_URL: z.coerce.string().url().optional(),
	VITE_LLM_MODEL: z.coerce.string().min(1).optional(),

	// auth (public SPA config)
	VITE_AUTH0_DOMAIN: z.coerce.string().min(1),
	VITE_AUTH0_CLIENT_ID: z.coerce.string().min(1),
	VITE_AUTH0_AUDIENCE: z.preprocess(
		(val) => (val === "" ? undefined : val),
		z.coerce.string().url().optional(),
	),

	// AWS (public identifiers)
	VITE_S3_REGION: z.coerce.string().min(1),
	VITE_S3_BUCKET_NAME: z.coerce
		.string()
		.min(1)
		.default("illumi-proto-protected"),

	// app metadata
	VITE_APP_VERSION: z.preprocess(
		(val) => (val === "" || val === undefined ? "0.0.1" : val),
		z.coerce.string().min(1),
	),
	VITE_COLLECT_NOTES_URL: z.coerce
		.string()
		.url()
		.default("https://forms-proto.illumi.systems/whiteboards"),

	// feature flags
	VITE_DEBUG_MODE: booleanEnv.default("false"),
	VITE_ENABLE_LOCAL_SYNC: booleanEnv,
	VITE_ENABLE_DEBUG_MENU: booleanEnv.default("false"),
	VITE_ENABLE_DESKTOP_BROWSER_LOGIN: booleanEnv.default("false"),

	// integrations
	VITE_SENTRY_DSN: z.coerce.string().url().optional(),
	VITE_SENTRY_ENVIRONMENT_NAME: z.coerce.string().optional(),
	VITE_GOOGLE_ANALYTICS_ID: z.coerce.string().optional(),
	VITE_PUBLIC_POSTHOG_HOST: z.coerce.string().url().optional(),
	VITE_PUBLIC_POSTHOG_KEY: z.coerce.string().optional(),
	VITE_STRIPE_PUBLISHABLE_KEY: z.coerce.string().startsWith("pk_").optional(),
	VITE_DISCORD_URL: z.preprocess(
		(val) => (val === "" ? undefined : val),
		z.coerce.string().url().optional(),
	),
	VITE_DISCORD_DEFECTS_URL: z.preprocess(
		(val) => (val === "" ? undefined : val),
		z.coerce.string().url().optional(),
	),

	// desktop
	VITE_UPDATE_CHANNEL: z.coerce.string().min(1).default("latest"),
	VITE_STORE_URL: z.coerce.string().url().optional(),
	VITE_DESKTOP_DOWNLOAD_URL: z.coerce.string().url().optional(),
});

export type Env = z.infer<typeof envSchema>;

export function validateEnv(
	rawEnv: Record<string, unknown> = import.meta.env,
): Env {
	const parsed = envSchema.safeParse(rawEnv);

	if (!parsed.success) {
		console.error("❌ Invalid environment variables:", parsed.error.format());
		throw new Error("Invalid environment variables");
	}

	return parsed.data;
}
